
import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Filter, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Class } from '@/types/classTypes';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface CustomCalendarHeaderProps {
  selectedMonth: Date;
  viewType: 'month' | 'week';
  filterType: string;
  classes: Class[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onFilterChange: (value: string) => void;
  onViewChange: (view: 'month' | 'week') => void;
}

export const CustomCalendarHeader: React.FC<CustomCalendarHeaderProps> = ({
  selectedMonth,
  viewType,
  filterType,
  classes,
  onPrevMonth,
  onNextMonth,
  onFilterChange,
  onViewChange
}) => {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-gray-100/60 shadow-sm">
      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onPrevMonth}
            aria-label="Previous month"
            className="hover:bg-gray-100/80 transition-colors rounded-l-lg"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </Button>
          
          <div className="flex items-center px-4 py-2 bg-white border border-gray-100 rounded-md shadow-sm">
            <Calendar className="h-4 w-4 text-slate-500 mr-2" />
            <h3 className="text-lg font-medium min-w-[140px] text-center text-slate-800">
              {format(selectedMonth, 'MMMM yyyy')}
            </h3>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onNextMonth}
            aria-label="Next month"
            className="hover:bg-gray-100/80 transition-colors rounded-r-lg"
          >
            <ChevronRight className="h-5 w-5 text-slate-600" />
          </Button>
        </div>
        
        <ToggleGroup 
          type="single" 
          value={viewType} 
          onValueChange={(value) => value && onViewChange(value as 'month' | 'week')}
          className="hidden sm:flex bg-white border border-gray-200 p-1 rounded-md shadow-sm"
        >
          <ToggleGroupItem 
            value="month" 
            aria-label="View month" 
            className="text-xs px-3 py-1 data-[state=on]:bg-gray-100/70 data-[state=on]:text-slate-800"
          >
            Month
          </ToggleGroupItem>
          <ToggleGroupItem 
            value="week" 
            aria-label="View week" 
            className="text-xs px-3 py-1 data-[state=on]:bg-gray-100/70 data-[state=on]:text-slate-800"
          >
            Week
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <Select value={filterType} onValueChange={onFilterChange}>
          <SelectTrigger className="w-[200px] h-9 text-sm bg-white border border-gray-200 rounded-md shadow-sm">
            <SelectValue placeholder="Filter classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes & Events</SelectItem>
            <SelectItem value="classes">Regular Classes Only</SelectItem>
            <SelectItem value="events">Events Only</SelectItem>
            {classes.map(cls => (
              <SelectItem key={cls.id} value={cls.id}>
                {cls.name} {cls.is_event ? "(Event)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
